import React, { useState, useEffect, useRef } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import DrawerShell from './DrawerShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, User, Plus, Loader2, UserPlus } from 'lucide-react';

export function PatientSearchModal({ open, onOpenChange }) {
  const { patients, loadPatients, createPatient, createConsultation } = useConsultation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewPatient, setShowNewPatient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newPatient, setNewPatient] = useState({ name: '', age: '', gender: 'male', phone: '' });
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    const debounce = setTimeout(() => loadPatients(searchQuery), 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, loadPatients]);

  const handleSelectPatient = async (patientId) => {
    setLoading(true);
    const result = await createConsultation(patientId);
    setLoading(false);
    if (result.success) {
      onOpenChange(false);
      setSearchQuery('');
    }
  };

  const handleCreatePatient = async () => {
    if (!newPatient.name || !newPatient.age || !newPatient.gender) return;
    setLoading(true);
    setError('');
    try {
      const result = await createPatient({ ...newPatient, age: parseInt(newPatient.age) });
      if (result.success) {
        await createConsultation(result.patient.id);
        onOpenChange(false);
        setShowNewPatient(false);
        setNewPatient({ name: '', age: '', gender: 'male', phone: '' });
      } else {
        setError(result.error || 'Failed to create patient');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    }
    setLoading(false);
  };

  return (
    <DrawerShell
      open={open}
      onClose={() => onOpenChange(false)}
      eyebrow={showNewPatient ? 'New record' : 'Patient lookup'}
      title={showNewPatient ? 'Add a new patient' : 'Start a consultation'}
      subtitle={showNewPatient ? 'Captured fields will sync to MRN immediately' : 'Find an existing record or add a brand-new one'}
      accent="#1d4ed8"
      icon={showNewPatient ? <UserPlus className="h-5 w-5" /> : <Search className="h-5 w-5" />}
      width="md"
      testId="patient-search-modal"
    >
      {!showNewPatient ? (
        <div className="p-5 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, or MRN..."
              className="pl-10"
              data-testid="patient-search-input"
            />
          </div>

          <div className="max-h-[55vh] overflow-auto space-y-1 -mx-1 px-1">
            {patients.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-bold">No patients found</p>
                <p className="text-xs">Try a different search or add a new patient</p>
              </div>
            ) : (
              patients.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => handleSelectPatient(patient.id)}
                  disabled={loading}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 text-left transition-colors border border-transparent hover:border-blue-200"
                  data-testid={`patient-option-${patient.id}`}
                >
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-blue-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-zinc-900 truncate">{patient.name}</p>
                    <p className="text-xs text-zinc-500">
                      {patient.age}y · {patient.gender} · {patient.medical_record_number || 'No MRN'}
                    </p>
                  </div>
                  {patient.phone && (
                    <span className="text-xs text-zinc-400">{patient.phone}</span>
                  )}
                </button>
              ))
            )}
          </div>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => setShowNewPatient(true)}
            data-testid="new-patient-btn"
          >
            <Plus className="h-4 w-4" />
            Add New Patient
          </Button>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label>Patient Name *</Label>
            <Input
              value={newPatient.name}
              onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
              placeholder="Full name"
              autoFocus
              data-testid="new-patient-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Age *</Label>
              <Input
                type="number"
                value={newPatient.age}
                onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value })}
                placeholder="Age"
                data-testid="new-patient-age"
              />
            </div>
            <div className="space-y-2">
              <Label>Gender *</Label>
              <Select
                value={newPatient.gender}
                onValueChange={(value) => setNewPatient({ ...newPatient, gender: value })}
              >
                <SelectTrigger data-testid="new-patient-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Phone (Optional)</Label>
            <Input
              value={newPatient.phone}
              onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
              placeholder="Phone number"
              data-testid="new-patient-phone"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowNewPatient(false)}
              data-testid="cancel-new-patient"
            >
              Back
            </Button>
            <Button
              className="flex-1 bg-blue-700 hover:bg-blue-800"
              onClick={handleCreatePatient}
              disabled={loading || !newPatient.name || !newPatient.age}
              data-testid="create-patient-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create & Start'}
            </Button>
          </div>
        </div>
      )}
    </DrawerShell>
  );
}
